import type { CSSProperties } from "react"
import { useEffect, useState } from "react"

import { cn } from "@productlane/lib/src/cn"

// Mirrors .swap-text-out and .swap-text-in in src/styles/shared.css, and the
// same pair in apps/widget-app/src/style/animations.css.
export const SWAP_TEXT_OUT_MS = 110
export const SWAP_TEXT_IN_MS = 160

/** How long both halves of the swap take, so the outgoing copy can unmount. */
export const SWAP_TEXT_TOTAL_MS = SWAP_TEXT_OUT_MS + SWAP_TEXT_IN_MS

// The fade overlaps both copies, so it is over when the new one has arrived.
export const SWAP_TEXT_FADE_MS = 240

export type SwapTextTransition = "slide" | "fade"

const SWAP_CLASSES: Record<SwapTextTransition, { out: string; in: string }> = {
	slide: { out: "swap-text-out", in: "swap-text-in" },
	fade: { out: "swap-text-fade-out", in: "swap-text-fade-in" },
}

/**
 * A line of text that changes wording mid-session. The old copy leaves to the
 * left before the new one arrives from the right, so the reader sees the change
 * instead of finding a different sentence where the last one was. The outgoing
 * copy is absolutely positioned, so nothing around it moves.
 */
export const SwapText = ({
	value,
	className,
	textClassName,
	style,
	transition = "slide",
}: {
	value: string
	className?: string
	textClassName?: string
	style?: CSSProperties
	// "fade" suits a line that settles once, like a title arriving; "slide"
	// reads as progress, like the agent's wait line.
	transition?: SwapTextTransition
}) => {
	const classes = SWAP_CLASSES[transition]
	const [{ current, previous }, setSwap] = useState<{
		current: string
		previous: string | null
	}>({ current: value, previous: null })

	useEffect(() => {
		setSwap((swap) =>
			swap.current === value
				? swap
				: { current: value, previous: swap.current },
		)
	}, [value])

	useEffect(() => {
		if (previous === null) return
		const timeout = setTimeout(
			() => setSwap((swap) => ({ ...swap, previous: null })),
			transition === "fade" ? SWAP_TEXT_FADE_MS : SWAP_TEXT_TOTAL_MS,
		)
		return () => clearTimeout(timeout)
	}, [previous, current, transition])

	return (
		<span className={cn("relative block", className)} style={style}>
			{previous !== null && (
				<span
					aria-hidden
					className={cn(classes.out, "absolute inset-0 block truncate")}
				>
					<span className={textClassName}>{previous}</span>
				</span>
			)}
			<span
				key={current}
				className={cn("block truncate", previous !== null && classes.in)}
			>
				<span className={textClassName}>{current}</span>
			</span>
		</span>
	)
}
